import { FilterSaveState, TaskViewEditableFields } from 'components/Tasks/TaskViews/TaskViewTypes';
import { TaskViewDTO, TaskViewUpdateDTO } from 'models/dto/tasks/taskViewDTO';
import {
  apiGetTaskViews,
  apiShareTaskView,
  apiUpdateTaskView,
  getLatestRevisionFromError,
} from 'services/Api/taskViewService';
import AppError from 'utils/appError';
import { logTaskViewDiagnostic } from 'utils/taskViewDiagnostics';
import { isDuplicateViewNameError } from './TaskViewHelpers';

const saveDelayMs = 500;

// Revisions are UpdatedAt ticks as strings. Comparing them as numbers loses precision above 2^53.
const isNewerRevision = (candidate: string | undefined, current: string | undefined): boolean => {
  if (!candidate) return false;
  if (!current) return true;
  if (candidate.length !== current.length) return candidate.length > current.length;

  return candidate > current;
};

// A field in `newer` replaces the same field in `older`.
const mergeEdits = (older: TaskViewEditableFields, newer: TaskViewEditableFields): TaskViewEditableFields => ({
  label: newer.label ?? older.label,
  filterValues: newer.filterValues ?? older.filterValues,
});

const hasEdits = (edits: TaskViewEditableFields): boolean =>
  edits.label !== undefined || edits.filterValues !== undefined;

interface ISavedView {
  server: TaskViewDTO;
  pending: TaskViewEditableFields;
  sending?: TaskViewEditableFields;
  timer?: ReturnType<typeof setTimeout>;
  // Last queued save or share. The next one starts after it.
  tail?: Promise<unknown>;
}

export interface ITaskViewSaveQueueDeps {
  getAccessToken: () => Promise<string>;
  renderView: (server: TaskViewDTO, edits: TaskViewEditableFields) => void;
  removeView: (taskViewId: number) => void;
  setSaveState: (taskViewId: number, state: FilterSaveState) => void;
  notifySaveFailed: (retry: () => void) => void;
}

// Saves task views, one request at a time per view, with the revision the server last confirmed.
export class TaskViewSaveQueue {
  private readonly views = new Map<number, ISavedView>();

  constructor(private readonly deps: ITaskViewSaveQueueDeps) {}

  isTracked(taskViewId: number): boolean {
    return this.views.has(taskViewId);
  }

  trackedIds(): number[] {
    return [...this.views.keys()];
  }

  serverVersion(taskViewId: number): TaskViewDTO | undefined {
    return this.views.get(taskViewId)?.server;
  }

  // Edits the server does not have yet, including the ones in a save that is still running.
  edits(taskViewId: number): TaskViewEditableFields {
    const view = this.views.get(taskViewId);

    return view ? mergeEdits(view.sending ?? {}, view.pending) : {};
  }

  // Starts tracking a view, or takes `dto` when it is newer. Returns the version now tracked.
  track(dto: TaskViewDTO): TaskViewDTO {
    const view = this.views.get(dto.taskViewId);
    if (!view) {
      this.views.set(dto.taskViewId, { server: dto, pending: {} });

      return dto;
    }
    if (isNewerRevision(dto.revision, view.server.revision)) view.server = dto;

    return view.server;
  }

  // A version pushed by the hub. The tab's own save comes back with a revision it already has.
  receive(dto: TaskViewDTO): void {
    const view = this.views.get(dto.taskViewId);
    if (!view) return;
    if (!isNewerRevision(dto.revision, view.server.revision)) {
      logTaskViewDiagnostic('view.receive.stale', {
        taskViewId: dto.taskViewId,
        revision: dto.revision,
        current: view.server.revision,
      });

      return;
    }
    logTaskViewDiagnostic('view.receive.applied', { taskViewId: dto.taskViewId, revision: dto.revision });
    view.server = dto;
    this.render(dto.taskViewId);
  }

  forget(taskViewId: number): void {
    const view = this.views.get(taskViewId);
    if (!view) return;
    logTaskViewDiagnostic('view.forget', { taskViewId });
    clearTimeout(view.timer);
    this.views.delete(taskViewId);
    // A pending edit set the chip to 'saving'; nothing will finish it now.
    this.deps.setSaveState(taskViewId, 'idle');
  }

  clear(): void {
    this.views.forEach((view) => clearTimeout(view.timer));
    this.views.clear();
  }

  edit(taskViewId: number, fields: TaskViewEditableFields): void {
    const view = this.views.get(taskViewId);
    if (!view || !hasEdits(fields)) return;
    view.pending = mergeEdits(view.pending, fields);
    logTaskViewDiagnostic('view.edit', { taskViewId, fields: Object.keys(fields), pending: view.pending });
    this.deps.setSaveState(taskViewId, 'saving');
    clearTimeout(view.timer);
    view.timer = setTimeout(() => void this.queueSave(taskViewId), saveDelayMs);
  }

  // Saves right away. Rejects only for a duplicate name; the caller shows it.
  async flush(taskViewId: number): Promise<void> {
    const duplicateNameError = await this.queueSave(taskViewId);
    if (duplicateNameError) throw duplicateNameError;
  }

  retry(taskViewId: number): void {
    const view = this.views.get(taskViewId);
    if (view && hasEdits(view.pending)) void this.queueSave(taskViewId);
  }

  async share(taskViewId: number, isShared: boolean): Promise<void> {
    await this.enqueue(taskViewId, async (view) => {
      logTaskViewDiagnostic('view.share', { taskViewId, isShared, revision: view.server.revision });
      const token = await this.deps.getAccessToken();
      const updated = await this.sendWithConflictRetry(taskViewId, token, () =>
        apiShareTaskView(taskViewId, isShared, view.server.revision, token),
      );
      // Deleted or reset while the request was out.
      if (!this.isCurrent(taskViewId, view)) return;
      if (!updated) {
        this.removeGoneView(taskViewId);
        throw new AppError('Task view no longer exists', '404');
      }
      this.track(updated);
      this.render(taskViewId);
      logTaskViewDiagnostic('view.share.done', { taskViewId, isShared, revision: updated.revision });
    });
  }

  private queueSave(taskViewId: number): Promise<AppError | undefined> {
    const view = this.views.get(taskViewId);
    if (!view) return Promise.resolve(undefined);
    clearTimeout(view.timer);
    view.timer = undefined;

    return this.enqueue(taskViewId, (current) => this.save(taskViewId, current));
  }

  private enqueue<T>(taskViewId: number, task: (view: ISavedView) => Promise<T>): Promise<T | undefined> {
    const view = this.views.get(taskViewId);
    if (!view) return Promise.resolve(undefined);
    const next = (view.tail ?? Promise.resolve())
      .catch(() => undefined)
      .then(() => (this.isCurrent(taskViewId, view) ? task(view) : undefined));
    view.tail = next;

    return next;
  }

  private async save(taskViewId: number, view: ISavedView): Promise<AppError | undefined> {
    // An earlier save in the queue already took these edits.
    if (!hasEdits(view.pending)) {
      logTaskViewDiagnostic('view.save.nothingToSend', { taskViewId });

      return undefined;
    }

    view.sending = view.pending;
    view.pending = {};
    this.deps.setSaveState(taskViewId, 'saving');
    logTaskViewDiagnostic('view.save.put', { taskViewId, sending: view.sending, revision: view.server.revision });
    try {
      const token = await this.deps.getAccessToken();
      const updated = await this.sendWithConflictRetry(taskViewId, token, () =>
        apiUpdateTaskView(taskViewId, this.buildUpdate(view), token),
      );
      if (!this.isCurrent(taskViewId, view)) {
        logTaskViewDiagnostic('view.save.dropped', { taskViewId });

        return undefined;
      }
      view.sending = undefined;
      if (!updated) {
        this.removeGoneView(taskViewId);

        return undefined;
      }
      // Render even when the hub delivered this revision first, so the saved fields show the
      // server's value (it trims the name, for example).
      this.track(updated);
      this.render(taskViewId);
      logTaskViewDiagnostic('view.save.done', {
        taskViewId,
        revision: updated.revision,
        stillPending: hasEdits(view.pending),
      });
      this.saveAgainOr(taskViewId, view, 'saved');

      return undefined;
    } catch (err) {
      if (!this.isCurrent(taskViewId, view)) {
        logTaskViewDiagnostic('view.save.dropped', { taskViewId });

        return undefined;
      }

      return this.handleSaveError(taskViewId, view, AppError.fromApiError(err));
    }
  }

  private handleSaveError(taskViewId: number, view: ISavedView, error: AppError): AppError | undefined {
    const sent = view.sending ?? {};
    view.sending = undefined;

    if (error.code === '404') {
      this.removeGoneView(taskViewId);

      return undefined;
    }

    if (isDuplicateViewNameError(error)) {
      logTaskViewDiagnostic('view.save.duplicateName', { taskViewId, code: error.code, sent });
      // Drop the rejected name. A name typed during the save sits in `pending`
      // and goes out next.
      view.pending = mergeEdits({ filterValues: sent.filterValues }, view.pending);
      this.render(taskViewId);
      this.saveAgainOr(taskViewId, view, 'idle');

      return error;
    }

    // Offline, 401/403, 5xx: keep the edits for the next edit or "Try again"; no error dialog.
    logTaskViewDiagnostic('view.save.error', { taskViewId, code: error.code, sent });
    view.pending = mergeEdits(sent, view.pending);
    this.deps.setSaveState(taskViewId, 'error');
    this.deps.notifySaveFailed(() => this.retry(taskViewId));

    return undefined;
  }

  private saveAgainOr(taskViewId: number, view: ISavedView, doneState: FilterSaveState): void {
    if (hasEdits(view.pending)) {
      void this.queueSave(taskViewId);
    } else {
      this.deps.setSaveState(taskViewId, doneState);
    }
  }

  // On 409 another tab saved first: take the current row and send once more. `send` builds its
  // payload on every call, so the retry uses the new revision.
  // Returns undefined if the view is gone.
  private async sendWithConflictRetry(
    taskViewId: number,
    token: string,
    send: () => Promise<TaskViewDTO>,
  ): Promise<TaskViewDTO | undefined> {
    try {
      return await send();
    } catch (err) {
      if ((err as AppError)?.code !== '409') throw err;

      const latest = (await apiGetTaskViews(token)).find((v) => v.taskViewId === taskViewId);
      logTaskViewDiagnostic('view.conflict', {
        taskViewId,
        headerRevision: getLatestRevisionFromError(err),
        latestRevision: latest?.revision,
      });
      if (!latest) return undefined;
      this.receive(latest);

      return send();
    }
  }

  private buildUpdate(view: ISavedView): TaskViewUpdateDTO {
    const { server, sending = {} } = view;

    return {
      name: sending.label ?? server.name,
      filterValues: [...(sending.filterValues ?? server.filterValues)],
      sourceRoute: server.sourceRoute,
      parentViewId: server.parentViewId,
      revision: server.revision,
    };
  }

  private render(taskViewId: number): void {
    const view = this.views.get(taskViewId);
    if (view) this.deps.renderView(view.server, this.edits(taskViewId));
  }

  private removeGoneView(taskViewId: number): void {
    logTaskViewDiagnostic('view.save.gone', { taskViewId });
    this.forget(taskViewId);
    this.deps.removeView(taskViewId);
  }

  private isCurrent(taskViewId: number, view: ISavedView): boolean {
    return this.views.get(taskViewId) === view;
  }
}
